/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx, keyframes } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useContext, useRef, useState } from 'react';

import { onClickNavUrl } from '@coursera-web/cds-labs';

import createLoadableComponent from 'js/lib/createLoadableComponent';
import useRouter from 'js/lib/useRouter';

import { Button, Divider, Grid, Link, PageGridContainer, Typography2, breakpoints } from '@coursera/cds-core';
import type { IconProps, Theme } from '@coursera/cds-core';
import { ChevronDownIcon, ChevronUpIcon, CloseIcon, MenuIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { disableScrolling } from 'bundles/browse/utils';
import type * as Types from 'bundles/ent-website/__generated__/baseContentfulTypes';
import type { CtaFragment } from 'bundles/ent-website/__generated__/contentfulTypes';
import { EntWebsiteDomainContext } from 'bundles/ent-website/components/EntWebsiteDomainContext';
import { EntWebsiteNavDomainContext } from 'bundles/ent-website/components/EntWebsiteNavDomainContext';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { MENU_HEIGHT, MENU_HEIGHT_MOBILE, NAV_BREAKPOINT } from 'bundles/ent-website/components/EntWebsitePage';
import HeaderLogo from 'bundles/ent-website/components/HeaderLogo';
import { MetaNavigation, getMetaHeight, isMetaNavEnabled } from 'bundles/ent-website/components/MetaNavigation';
import PriorityCTA from 'bundles/ent-website/components/PriorityCTA';
import type { PROMOTIONS } from 'bundles/ent-website/components/Promotion';
import Promotion from 'bundles/ent-website/components/Promotion';
import EntWebsiteNavTag from 'bundles/ent-website/components/nav/EntWebsiteNavTag';
import MobileNav from 'bundles/ent-website/components/nav/MobileNav';
import { DOMAINS, NAV_LINKS, priorityCtaOverride } from 'bundles/ent-website/constants/navigation';
import type {
  LinkIndividualLink,
  LinkObject,
  LinkObjectChildren,
} from 'bundles/ent-website/contentful/core/Navigation';
import { eventingTransform } from 'bundles/ent-website/utils/eventingTransform';
import { isActive, isActiveParent } from 'bundles/ent-website/utils/headerUtils';
import EnterpriseGrowthExperiments from 'bundles/epic/clients/EnterpriseGrowth';
import SmartScrollWrapper from 'bundles/page-header/components/SmartScrollWrapper';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/ent-website';

const SOURCE_SANS = `'Source Sans Pro', Arial, sans-serif`;

export const links = (domain: keyof typeof DOMAINS): LinkObject[] => {
  const enableMetaNav = isMetaNavEnabled();
  // We want a A/B test, only of the Treatment of meta nav

  if (domain === DOMAINS.c4b || domain === DOMAINS.c4t) {
    let c4bLinks = NAV_LINKS[DOMAINS.c4b];
    const enableC4BNavPromo = enableMetaNav && EnterpriseGrowthExperiments.get('enableC4BNavPromo');

    if (enableC4BNavPromo) {
      c4bLinks = NAV_LINKS[`${DOMAINS.c4b}:enableC4BNavPromo`];
    }

    return c4bLinks;
  }

  if (domain === DOMAINS.c4g) {
    return NAV_LINKS[DOMAINS.c4g];
  }

  return NAV_LINKS[DOMAINS.c4b];
};

const AuthenticationOption = createLoadableComponent(
  () => import('bundles/user-account/components/AuthenticationOption')
);

type BlueScrimProps = {
  handleClick: (event: React.MouseEvent<HTMLElement>) => void;
  domain: keyof typeof DOMAINS;
};

function BlueScrim({ handleClick, domain }: BlueScrimProps) {
  const styles: Interpolation<Theme> = () => css`
    display: block;
    position: fixed;
    height: 100vh;
    left: 0;
    right: 0;
    background: var(--cds-color-blue-950);
    opacity: 0.8;
    z-index: 5;
    top: calc(${MENU_HEIGHT} + ${getMetaHeight({ domain })});
    ${breakpoints.down(NAV_BREAKPOINT)} {
      top: ${MENU_HEIGHT_MOBILE};
    }
  `;

  return <div aria-hidden data-testid="blue-scrim" onClick={handleClick} css={styles} />;
}

const styles: Record<string, Interpolation<Theme>> = {
  headerContainer: css`
    position: fixed;
    width: 100%;
    z-index: 10;
    margin-top: 0;
    transition: margin-top 0.333s ease-in-out;
  `,
  header: () => css`
    position: relative;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: ${MENU_HEIGHT};
    background: white;
    border-bottom: 1px solid rgb(0 0 0 / 10%);
    z-index: 10;
    /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
    background: white;
    top: 0;

    ${breakpoints.down(NAV_BREAKPOINT)} {
      height: ${MENU_HEIGHT_MOBILE};
      top: 0;
    }
  `,
  logo: css`
    margin-right: 72px;
    line-height: 1;

    > img {
      vertical-align: baseline;
    }
  `,
  parentContainer: css`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: 0;
    padding: 0;
    align-items: center;
  `,
  parentRight: css`
    flex-grow: 1;
    display: flex;
    justify-content: end;
  `,
  link: () => css`
    display: inline-block;
    padding: var(--cds-spacing-200);

    a:hover,
    a:active {
      text-decoration: none;
      color: var(--cds-color-blue-700) !important;
    }
  `,
  currentUrlMarker: () => css`
    color: var(--cds-color-grey-975) !important;
    font-weight: 600;
  `,
  currentUrlChildMarker: css`
    * {
      text-decoration: underline !important;
    }
  `,
};

type MegaMenuPanelProps = {
  link: LinkObject;
  domain: keyof typeof DOMAINS;
};

type HeaderLinkProps = {
  navLinks: LinkObject[];
  link: LinkObject;
  expanded?: boolean;
  handleClick: (event: React.MouseEvent<HTMLElement>) => void;
  domain: keyof typeof DOMAINS;
};

const opacityFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export function MegaMenuPanel({ link, domain }: MegaMenuPanelProps) {
  const megaMenuPanelStyles: Record<string, Interpolation<Theme>> = {
    fixedContainer: () => css`
      position: fixed;
      left: 0;
      right: 0;
      z-index: 10;
      background: white;
      overflow-y: scroll;

      .hide-smart-scroll-container & {
        top: ${MENU_HEIGHT};
      }

      .show-smart-scroll-container & {
        top: calc(${MENU_HEIGHT} + ${getMetaHeight({ domain })});
      }
      ${breakpoints.down(NAV_BREAKPOINT)} {
        top: ${MENU_HEIGHT_MOBILE};
      }
    `,
    navContainer: () => css`
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      width: 100%;
      max-width: 1200px;
      padding-top: var(--cds-spacing-600);
      padding-bottom: var(--cds-spacing-800);
      animation: ${opacityFadeIn} 0.333s linear;

      > div {
        &:nth-child(1) {
          flex-grow: 1;
          max-width: 640px;
        }

        &:nth-child(2) {
          padding-left: var(--cds-spacing-400);
        }
      }
    `,
    navContainerActive: css`
      opacity: 1;
    `,
    oneColumn: css`
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;

      > * {
        width: 100%;
      }
    `,
    twoColumn: css`
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;

      > * {
        width: 50%;
      }
    `,
    divider: () => css`
      margin-top: 20px;
      margin-bottom: var(--cds-spacing-600);
    `,
    linkItem: () => css`
      padding-right: var(--cds-spacing-400);
      margin-bottom: var(--cds-spacing-400);
      cursor: pointer;

      &:hover p {
        &:nth-child(2) {
          text-decoration: underline !important;
          color: var(--cds-color-blue-700) !important;
        }
      }
    `,
    heading: css`
      font-family: ${SOURCE_SANS};
      font-size: 24px;
      font-weight: 400;
      line-height: 36px;
      letter-spacing: -0.02em;
      text-align: left;
    `,
    subHeading: () => css`
      font-family: ${SOURCE_SANS};
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: -0.02em;
      text-align: left;
      color: var(--cds-color-grey-600);
    `,
    headingContainer: css`
      padding-bottom: 1px;
    `,
    itemHeading: css`
      font-family: ${SOURCE_SANS};
      font-size: 14px;
      font-weight: 600;
      line-height: 21px;
      letter-spacing: -0.03em;
      text-align: left;
    `,
    itemSubHeading: () => css`
      margin-top: 4px;
      font-family: ${SOURCE_SANS};
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: -0.02em;
      text-align: left;
      color: var(--cds-color-grey-600);
    `,
  };

  const router = useRouter();
  const pathname = router.location.pathname;
  const track = useTracker();

  const handleLinkClick = (e: React.MouseEvent<HTMLElement>, item: LinkIndividualLink | null) => {
    track('click_megamenu_item', {
      megamenuSection: eventingTransform(link.text),
      megamenuItemName: eventingTransform(item?.ctaText || ''),
      megamenuItemLink: item?.ctaTarget || '#',
      megamenuItemType: 'other',
    });
    if (item?.shouldOpenInNewWindow) {
      return;
    }
    onClickNavUrl(router, item?.ctaTarget ?? '#')(e);
  };

  function renderItems(childObj: LinkObjectChildren, idx: number) {
    return (
      <div css={[idx === 0 ? megaMenuPanelStyles.twoColumn : megaMenuPanelStyles.oneColumn]}>
        {childObj.items?.map((item) => {
          const trackingName = eventingTransform(`navigation_mega_menu_${link.text}_${item?.ctaText}`);
          return (
            <div css={megaMenuPanelStyles.linkItem} key={item?.ctaText}>
              <Link
                key={item?.sys?.id}
                component={TrackedA}
                trackingName={trackingName}
                variant="quiet"
                href={item?.ctaTarget ?? '#'}
                onClick={(e) => handleLinkClick(e, item)}
                rel={item?.shouldOpenInNewWindow ? 'noreferrer noopenner' : undefined}
                target={item?.ctaTarget ?? undefined}
                css={isActive(pathname, item?.ctaTarget ?? '') ? styles.currentUrlChildMarker : null}
              >
                <Grid container direction="row" alignItems="center" css={megaMenuPanelStyles.headingContainer}>
                  <Typography2 component="p" css={megaMenuPanelStyles.itemHeading}>
                    {item?.ctaText}
                  </Typography2>

                  {item?.showNewBadge && <EntWebsiteNavTag label={_t('New')} />}
                </Grid>
                {item?.ctaDescription && (
                  <Typography2 component="p" css={megaMenuPanelStyles.itemSubHeading}>
                    {item.ctaDescription}
                  </Typography2>
                )}
              </Link>
            </div>
          );
        })}
      </div>
    );
  }

  function renderPromotion(childObj: LinkObjectChildren) {
    return <Promotion promotion={childObj.promotion} />;
  }

  return (
    <div css={megaMenuPanelStyles.fixedContainer}>
      <PageGridContainer maxWidth="lg">
        <Grid item xs={12}>
          <div css={[megaMenuPanelStyles.navContainer, megaMenuPanelStyles.navContainerActive]}>
            {link.children?.map((childObj, idx) => {
              return (
                <div key={childObj?.sys?.id}>
                  {childObj.heading && (
                    <>
                      <Typography2 component="p" css={megaMenuPanelStyles.heading}>
                        {childObj.heading}
                      </Typography2>
                      <Typography2 component="p" css={megaMenuPanelStyles.subHeading}>
                        {childObj.description}
                      </Typography2>
                      <Divider css={megaMenuPanelStyles.divider} />
                    </>
                  )}
                  {renderPromotion(childObj)}
                  {renderItems(childObj, idx)}
                </div>
              );
            })}
          </div>
        </Grid>
      </PageGridContainer>
    </div>
  );
}

const headerLinkStyles: Record<string, Interpolation<Theme>> = {
  link: () => css`
    font-family: ${SOURCE_SANS};
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0;
    text-align: left;
    vertical-align: middle;
    cursor: pointer;
    color: var(--cds-color-grey-600) !important;

    &:hover {
      text-decoration: underline !important;
    }
  `,
  linkExpanded: () => css`
    color: var(--cds-color-blue-700) !important;
    text-decoration: none;
  `,
};

function HeaderLink({ link, navLinks, expanded, domain, handleClick }: HeaderLinkProps) {
  const router = useRouter();
  const pathname = router.location.pathname;
  const track = useTracker();
  let icon: React.ReactElement<IconProps> | undefined;
  if (link.children?.length) {
    if (expanded) {
      icon = <ChevronUpIcon size="small" />;
    } else {
      icon = <ChevronDownIcon size="small" />;
    }
  }

  const trackingNameLogin = eventingTransform(`navigation_toggle_${link.text}`);

  const getButtonName = () => (link.text === 'For Teams' ? 'ent_website_for_teams' : 'ent_website_compare_plans');

  const handleLinkClick = (e: React.MouseEvent<HTMLElement>) => {
    if (link.children?.length) {
      handleClick(e);
    } else {
      track('click_button', {
        button: {
          name: getButtonName(),
        },
      });
      onClickNavUrl(router, link.href ?? '')(e);
    }
  };

  return (
    <div css={styles.link}>
      <Link
        component={TrackedA}
        trackingName={trackingNameLogin}
        href={link.href ?? '#'}
        variant="quiet"
        onClick={handleLinkClick}
        css={[
          headerLinkStyles.link,
          expanded ? headerLinkStyles.linkExpanded : null,
          isActiveParent(pathname, link, navLinks) ? styles.currentUrlMarker : null,
        ]}
        icon={icon}
        iconPosition="after"
      >
        {link.text}
      </Link>

      {expanded && <MegaMenuPanel link={link} domain={domain} />}
    </div>
  );
}

type HeaderLinksProps = {
  domain: keyof typeof DOMAINS;
  navLinks: LinkObject[];
};

const headerLinksStyles: Record<string, Interpolation<Theme>> = {
  parentLeftLinkContainer: () => css`
    justify-content: start;
    display: flex;
    flex-grow: 1;

    ${breakpoints.down(NAV_BREAKPOINT)} {
      display: none;
    }
  `,
};

function HeaderLinks({ domain, navLinks }: HeaderLinksProps) {
  const [expanded, setExpanded] = useState('');
  const track = useTracker();
  function toggleExpanded(text: string) {
    if (expanded === text) {
      disableScrolling(false);
      setExpanded('');
    } else {
      disableScrolling(true);
      setExpanded(text);
    }
  }

  const closeExpanded = () => {
    disableScrolling(false);
    setExpanded('');
  };

  return (
    <div data-testid="ew_header_links" css={headerLinksStyles.parentLeftLinkContainer}>
      {expanded && <BlueScrim handleClick={closeExpanded} domain={domain} />}

      {navLinks.map((link) => (
        <HeaderLink
          navLinks={navLinks}
          link={link}
          domain={domain}
          key={link?.sys?.id}
          expanded={expanded === link.text}
          handleClick={(event: React.MouseEvent<HTMLElement>) => {
            track('open_navigation_menu', {
              navigationType: 'megamenu',
              navigationName: eventingTransform(link.text),
            });
            toggleExpanded(link.text);
            event.preventDefault();
          }}
        />
      ))}
    </div>
  );
}

export type ContentfulHeaderData = Types.Maybe<
  { __typename?: 'EntWebsiteCustomPageHeader' } & Pick<
    Types.EntWebsiteCustomPageHeader,
    'logoTarget' | 'logoHeight' | 'hideMegaMenu' | 'hideCta'
  > & {
      logo?: Types.Maybe<{ __typename?: 'Asset' } & Pick<Types.Asset, 'url'>>;
      cta?: Types.Maybe<{ __typename?: 'EntWebsiteCta' } & CtaFragment>;
    }
>;

type HeaderProps = {
  hasAuthentication?: boolean;
  domain: keyof typeof DOMAINS;
  navLinks: LinkObject[];
  primaryCta: LinkIndividualLink | undefined | null;
  contentfulHeaderData?: ContentfulHeaderData;
};

const headerActionsStyles: Record<string, Interpolation<Theme>> = {
  parentRightLinkContainer: () => css`
    ${breakpoints.down(NAV_BREAKPOINT)} {
      display: none;
    }
  `,
  parentRightLinkContainerMobile: () => css`
    ${breakpoints.up(NAV_BREAKPOINT)} {
      display: none;
    }
  `,
  hamburgerMenu: css`
    color: var(--cds-color-neutral-primary);
    padding: var(--cds-spacing-100);
  `,
};

function HeaderActions({ hasAuthentication, domain, navLinks, primaryCta, contentfulHeaderData }: HeaderProps) {
  const router = useRouter();
  const track = useTracker();

  const handleLoginClick = () => {
    const query = { ...(router?.location?.query || {}), authMode: 'login' };
    track('click_button', {
      button: {
        name: 'login',
      },
    });
    router.replace({
      pathname: router?.location?.pathname || '/',
      query,
    });
  };

  const [mobileExpanded, setMobileExpanded] = useState(false);

  const toggleMobileExpanded = () => {
    setMobileExpanded(!mobileExpanded);
  };

  const trackingNameLogin = eventingTransform('navigation_login');
  const trackingNameMobileMenu = eventingTransform('navigation_mobile_menu');

  return (
    <>
      {/* Hidden on mobile */}
      <div css={headerActionsStyles.parentRightLinkContainer}>
        {hasAuthentication && (
          <div css={styles.link}>
            <Button variant="ghost" onClick={handleLoginClick} component={TrackedA} trackingName={trackingNameLogin}>
              <Typography2 component="p" color="supportText" variant="bodySecondary">
                {_t('Login')}
              </Typography2>
            </Button>
          </div>
        )}

        {!contentfulHeaderData?.hideCta && priorityCtaOverride[domain] && (
          <PriorityCTA primaryCta={primaryCta} {...priorityCtaOverride[domain]} />
        )}
      </div>
      <div css={headerActionsStyles.parentRightLinkContainerMobile}>
        {!(contentfulHeaderData?.hideMegaMenu && contentfulHeaderData?.hideCta) && (
          <>
            <Button
              variant="ghost"
              onClick={toggleMobileExpanded}
              css={headerActionsStyles.hamburgerMenu}
              component={TrackedA}
              trackingName={trackingNameMobileMenu}
              data-testid="mobile-hamburger"
              ariaLabel={mobileExpanded ? _t('Close Menu') : _t('Open Menu')}
            >
              {mobileExpanded ? <CloseIcon size="medium" /> : <MenuIcon size="medium" />}
            </Button>

            <MobileNav
              mobileExpanded={mobileExpanded}
              domain={domain}
              navLinks={navLinks}
              primaryCta={primaryCta}
              hideCta={contentfulHeaderData?.hideCta}
              hideNavigationLinks={contentfulHeaderData?.hideMegaMenu}
            />
          </>
        )}
      </div>
    </>
  );
}

type EntWebsitePageHeaderProps = {
  hasAuthentication?: boolean;
};

function EntWebsitePageHeader({ hasAuthentication }: EntWebsitePageHeaderProps) {
  const { data, forceHideMetaNav } = React.useContext(EntWebsiteNavDomainContext);

  const megaMenuRef = useRef(null);
  const router = useRouter();
  const isHidden = false;
  const { domain } = useContext(EntWebsiteDomainContext);
  const calcStyles: Record<string, Interpolation<Theme>> = {
    headerContainerHidden: css`
      margin-top: -${getMetaHeight({ domain })};
    `,
  };
  const metaNavStyles: Record<string, Interpolation<Theme>> = {
    container: () => css`
      ${breakpoints.down(NAV_BREAKPOINT)} {
        display: none;
      }
    `,
  };

  const navItems = data?.entWebsiteNavDomainCollection?.items;
  // Custom Page Header takes priority
  const customPageHeader = data?.entWebsitePageAssemblyCollection?.items?.[0]?.customPageHeader;

  const navCollection =
    customPageHeader?.navigationCollection?.items && customPageHeader?.navigationCollection?.items?.length > 0
      ? customPageHeader?.navigationCollection
      : navItems?.[0]?.navigationCollection;

  const primaryCta: LinkIndividualLink | null | undefined = {
    ...(customPageHeader?.cta ?? navItems?.[0]?.primaryCta),
  };

  const mappedLinks = navCollection?.items?.map((linkNav): LinkObject => {
    // Query has a hard limit of 2
    const linkChildren = linkNav?.childrenCollection?.items?.map((linkChild): LinkObjectChildren => {
      // Query has a hard limit of 8
      const individualLinks = linkChild?.linksCollection?.items?.map((link): LinkIndividualLink | null => {
        return link;
      });
      return {
        heading: linkChild?.heading ?? '',
        description: linkChild?.description ?? '',
        promotion: linkChild?.promotion as keyof typeof PROMOTIONS,
        items: individualLinks,
        sys: { id: linkChild?.sys?.id },
      };
    });

    return {
      text: linkNav?.text ?? '',
      href: linkNav?.href ?? '',
      children: linkChildren,
      sys: { id: linkNav?.sys?.id },
    };
  });

  const mappedNavLinks = mappedLinks || links(domain);

  return (
    <>
      <SmartScrollWrapper
        className="rc-EntWebsitePageHeader-SmartScrollWrapper"
        containerHeight={parseInt(getMetaHeight(), 10)}
        delta={isMetaNavEnabled({ forceOverride: !forceHideMetaNav }) ? 50 : Number.MAX_VALUE}
        style={{ boxShadow: 'none', maxWidth: '100vw' }}
      >
        <header
          css={[styles.headerContainer, isHidden ? calcStyles.headerSlideHidden : null]}
          className="rc-EntWebsitePageHeader"
        >
          <AuthenticationOption
            preventModalClose={false}
            preventRegister={false}
            query={router && router.location.query}
            key="authenticationOption"
          />
          <div css={metaNavStyles.container}>
            <MetaNavigation forceOverride={!forceHideMetaNav} />
          </div>
          {/* Dont remove classname - it's used to calculate sticky header offset for Compare Plans Page */}
          <div css={styles.header}>
            <PageGridContainer maxWidth="lg">
              <Grid item xs={12}>
                <div css={styles.parentContainer}>
                  <HeaderLogo domain={domain} contentfulHeaderData={customPageHeader} />
                  {!customPageHeader?.hideMegaMenu && <HeaderLinks domain={domain} navLinks={mappedNavLinks} />}
                  <HeaderActions
                    contentfulHeaderData={customPageHeader}
                    domain={domain}
                    hasAuthentication={hasAuthentication}
                    navLinks={mappedNavLinks}
                    primaryCta={primaryCta}
                  />
                </div>
              </Grid>
            </PageGridContainer>

            <div ref={megaMenuRef} />
          </div>
        </header>
      </SmartScrollWrapper>
    </>
  );
}

export default EntWebsitePageHeader;
